import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import { useProject } from "../app/projectContext";

import {
  createStarterCoordinationRun,
  type StarterCoordinationRunCreateRequest,
  type StarterCoordinationRunResponse,
} from "../services/starterCoordination";

/**
 * Starter coordination study mutation (EOS-05a).
 *
 * Mirrors usePVSizing:
 * - One in-flight request at a time: a new submit aborts the previous one.
 * - The in-flight request is aborted on unmount so no state update lands on
 *   an unmounted component.
 * - The run is attached to the revision chosen in the top bar, read at submit time; with
 *   no project, or one without an open revision, nothing is sent and the run is unassigned.
 * - Every calculation is persisted as a run (Master Prompt A16, A20); the typed
 *   result and the run summary are exposed exactly as parsed, including a
 *   NO_SOLUTION result - a starter whose declared evidence does not meet the fault level
 *   is engineering evidence too.
 * - A linked fault run must belong to the revision the study is saved in; the backend
 *   says so (409) and the message reaches the page unchanged.
 *
 * The request is the whole run body, so the study's notes travel with it; the
 * form never puts a project revision in it.
 */
export function useStarterCoordination() {
  const controllerRef = useRef<AbortController | null>(null);
  const { activeRevisionId } = useProject();

  const mutation = useMutation<
    StarterCoordinationRunResponse,
    Error,
    StarterCoordinationRunCreateRequest
  >({
    mutationKey: ["electrical", "starter-coordination", "runs", "create"],
    mutationFn: (payload) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      return createStarterCoordinationRun(
        payload,
        controller.signal,
        activeRevisionId() ?? undefined,
      );
    },
  });

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const calculate = useCallback(
    (payload: StarterCoordinationRunCreateRequest) => mutation.mutateAsync(payload),
    [mutation],
  );

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    mutation.reset();
  }, [mutation]);

  return {
    calculate,
    reset,
    result: mutation.data?.result ?? null,
    run: mutation.data?.run ?? null,
    error: mutation.error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
}

export type UseStarterCoordinationResult = ReturnType<typeof useStarterCoordination>;
